import { useCaptureTreeSelectionStore } from "@/components/ui/tree/capture-tree/capture-tree-selection-store";
import {
  selectPathPoiIndices,
  useMapPlaceholderPositions,
} from "@/hooks/use-map-placeholder-positions";
import { useObjectBoundingBox } from "@/hooks/use-object-bounding-box";
import { SceneContextMenuData } from "@/modes/sheet-mode/scene-context-menu/use-scene-context-menu";
import { SheetModeRenderOrders } from "@/modes/sheet-mode/sheet-mode-render-orders";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import {
  ScreenSpaceOrthographicLineCollision,
  selectAncestor,
  selectChildrenDepthFirst,
} from "@faro-lotv/app-component-toolbox";
import { cyan } from "@faro-lotv/flat-ui";
import {
  GUID,
  IElementGenericImgSheet,
  IElementImg360,
  isIElementImg360,
  isIElementOdometryPath,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { isAncestor, MapPlaceholders } from "@faro-lotv/lotv";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { forwardRef, useCallback, useMemo, useState, type JSX } from "react";
import mergeRefs from "react-merge-refs";
import {
  Box3,
  CatmullRomCurve3,
  Color,
  Group,
  OrthographicCamera,
  Plane,
  Vector3,
} from "three";
import { OdometryPathPlaceholders } from "./odometry-path-placeholders";
import { selectTrajectoryWaypointPanoramas } from "./path-selectors";
import { SmoothLineRenderer } from "./smooth-line-renderer";
import {
  useOdometryPathMapPositions,
  useOdometryPathWalkPositions,
} from "./use-odometry-path-positions";

/** Line rendering parameters */
const POINTS_SEGMENT_FACTOR = 50;
const LINE_EVENT_WIDTH = 50;
const LINE_WIDTH = 3;
const LINE_BORDER_WIDTH = LINE_WIDTH + 2;

/** Offsets applied to the line to avoid z-fighting */
const LINE_HEIGHT_OFFSET = 0.1;
const LINE_HEIGHT_OFFSET_ACTIVE = 0.11;

/** Name to assign to a odometry path object/group so it can be recognized by the rendering pipeline */
export const ODOMETRY_PATH_OBJECT_NAME = "OdometryPath";

/** A waypoint clicked on a trajectory */
export type PathWaypointTarget = {
  /** The path the waypoint belongs to */
  path: IElementGenericOdometryPath;

  /** Position of the waypoint, flattened for display on a sheet */
  position: Vector3;

  /** Position of the waypoint at the elevation used to render it in walk mode, missing for pano-based waypoints */
  walkPosition?: Vector3;

  /** Direction the trajectory is heading at the waypoint, missing when the trajectory has a single point */
  direction?: Vector3;

  /** The panorama captured at the waypoint, missing for trajectories described by an odometry file */
  element?: IElementImg360;
};

/**
 * @returns the normalized direction the trajectory is heading at a given waypoint
 * @param positions the positions of all the waypoints of a trajectory
 * @param index the waypoint to compute the direction for
 */
function trajectoryDirectionAt(
  positions: Vector3[],
  index: number,
): Vector3 | undefined {
  const next = positions.at(index + 1);
  const direction = next
    ? next.clone().sub(positions[index])
    : positions[index].clone().sub(positions.at(index - 1) ?? positions[index]);
  return direction.lengthSq() > 0 ? direction.normalize() : undefined;
}

/**
 * Function used for when a room pano is on top of the path line and should receive the context menu instead of the path.
 *
 * @param ev The context-menu event from r3f
 * @param pathGroup The root group of the current path renderer
 * @returns true if any intersection in the event hits a room img360.
 */
function hasForeignWaypointIntersection(
  ev: ThreeEvent<MouseEvent>,
  pathGroup: Group | null,
): boolean {
  return ev.intersections.some(
    (i) =>
      i.object instanceof MapPlaceholders &&
      (!pathGroup || !isAncestor(i.object, pathGroup)),
  );
}

type OdometryPathRendererProps = {
  /** The section with typeHint "OdometryPath" to render */
  path: IElementGenericOdometryPath;

  /** The sheet whose elevation will be used to render the paths for */
  sheetForElevation?: IElementGenericImgSheet;

  /** The currently active video recording IDs */
  activeRecordingIds?: GUID[];

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Callback when the path is clicked */
  onPathClick?(
    event: ThreeEvent<MouseEvent>,
    path: IElementGenericOdometryPath,
    pathBbox: Box3,
  ): void;

  /** Callback when a pano placeholder is clicked */
  onPlaceholderClicked?(target: PathWaypointTarget): void;

  /** Callback when a context menu is requested */
  onSceneContextMenu?(contextMenuData: SceneContextMenuData): void;

  /** Callback when a pano placeholder is hovered */
  onPlaceholderHovered?(pano?: IElementImg360): void;
};

/** @returns a single interactive odometry path  */
export function OdometryPathRenderer({
  path,
  sheetForElevation,
  activeRecordingIds,
  clippingPlanes,
  onPathClick,
  onPlaceholderClicked: onPanoClicked,
  onSceneContextMenu,
  onPlaceholderHovered: onPanoHovered,
}: OdometryPathRendererProps): JSX.Element | null {
  // A path either groups the panoramas it was reconstructed from, or references an odometry file
  const odometryFilePath = isIElementOdometryPath(path) ? path : undefined;

  const panos = useAppSelector(
    selectChildrenDepthFirst(path, isIElementImg360),
    isEqual,
  );
  const panoPositions = useMapPlaceholderPositions(panos, sheetForElevation);

  // Select the panos to render as waypoints. We include also the promoted ones
  // since we are not showing them to user right now
  const odometryPanos = useAppSelector(
    selectTrajectoryWaypointPanoramas(path),
    isEqual,
  );

  // The trajectory waypoints visualized on top of a trajectory
  const panoPlaceholderPositions = useMapPlaceholderPositions(
    odometryPanos,
    sheetForElevation,
  );
  const placeholderPoiIndices = useAppSelector(
    selectPathPoiIndices(odometryPanos),
  );

  const filePositions = useOdometryPathMapPositions(
    odometryFilePath,
    sheetForElevation,
  );

  // Mirrors the elevation used by the walk-mode ribbon, so clicking a waypoint here lands at the same height
  const fileWalkPositions = useOdometryPathWalkPositions(
    odometryFilePath,
    sheetForElevation,
  );

  const positions = odometryFilePath ? filePositions : panoPositions;
  const placeholderPositions = odometryFilePath
    ? filePositions
    : panoPlaceholderPositions;

  // Auto-promotion is an odometry-only behavior gated behind the mobile-scanner feature flag.
  const isAutomaticWaypointMobileScannersEnabled = useAppSelector(
    selectHasFeature(Features.AutomaticWaypointMobileScanners),
  );

  const onClick = useCallback(
    (event: ThreeEvent<MouseEvent>, pathBbox: Box3) => {
      onPathClick?.(event, path, pathBbox);
    },
    [onPathClick, path],
  );

  const onPlaceholderClick = useCallback(
    (index: number) => {
      onPanoClicked?.({
        path,
        position: placeholderPositions[index],
        walkPosition: odometryFilePath ? fileWalkPositions[index] : undefined,
        direction: trajectoryDirectionAt(placeholderPositions, index),
        element: odometryPanos.at(index),
      });
    },
    [
      onPanoClicked,
      path,
      placeholderPositions,
      odometryFilePath,
      fileWalkPositions,
      odometryPanos,
    ],
  );

  const onPlaceholderContextMenu = useCallback(
    (index: number) => {
      const pano = odometryPanos.at(index);
      onSceneContextMenu?.(
        pano
          ? { target: pano }
          : { position: placeholderPositions[index], target: path },
      );
    },
    [onSceneContextMenu, odometryPanos, path, placeholderPositions],
  );

  const onPathContextMenu = useCallback(
    (position: Vector3) => {
      onSceneContextMenu?.({ position, target: path });
    },
    [onSceneContextMenu, path],
  );

  const onPlaceholderHovered = useCallback(
    (index: number | undefined) =>
      onPanoHovered?.(
        index === undefined ? undefined : odometryPanos.at(index),
      ),
    [onPanoHovered, odometryPanos],
  );

  const isActive = !!useAppSelector(
    selectAncestor(
      path,
      (element) => !!activeRecordingIds?.includes(element.id),
    ),
  );

  const hoveredIds = useCaptureTreeSelectionStore((s) => s.hoveredIds);
  const isParentHovered = !!useAppSelector(
    selectAncestor(path, (e) => hoveredIds.includes(e.id)),
  );

  if (positions.length === 0) return null;

  return (
    <PathRenderer
      positions={positions}
      placeholderPositions={placeholderPositions}
      poiIndices={placeholderPoiIndices}
      isActive={isActive}
      isParentHovered={isParentHovered}
      onClick={onClick}
      onPlaceholderClick={onPlaceholderClick}
      onPlaceholderContextMenu={onPlaceholderContextMenu}
      onPathContextMenu={onPathContextMenu}
      onHoveredPlaceholderChanged={onPlaceholderHovered}
      enableAutoPromotion={isAutomaticWaypointMobileScannersEnabled}
      clippingPlanes={clippingPlanes}
    />
  );
}

export type PathRendererProps = {
  /** List of positions of the placeholders describing the path */
  positions: Vector3[];

  /**
   * Positions to use for the placeholder icons. When provided, only these positions
   * are used for `OdometryPathPlaceholders` instead of the full `positions` list.
   */
  placeholderPositions?: Vector3[];

  /** List of indices within the 'position' array that represent points of interest */
  poiIndices?: number[];

  /** For each placeholder, a flag specifying if is locked */
  locked?: boolean[];

  /**
   * Number of intermediate point between two placeholders when drawing the curve
   *
   * @default 50
   */
  segmentFactor?: number;

  /** Whether to show the path in its active state  */
  isActive: boolean;

  /**
   * Whether to always render the trajectory line, ignoring the trajectories view option.
   *
   * @default false
   */
  forceTrajectoryVisible?: boolean;

  /**
   * Whether the user hovered the parent element in the project tree
   *
   * @default false
   */
  isParentHovered?: boolean;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /**
   * Whether to split waypoints into auto-promoted and dense ones. Only meaningful for mobile
   * odometry placeholders; other paths should keep every waypoint in a single dense layer.
   *
   * @default false
   */
  enableAutoPromotion?: boolean;

  /** Callback when the path is clicked */
  onClick?(event: ThreeEvent<MouseEvent>, pathBbox: Box3): void;

  /** Callback when a placeholder is clicked */
  onPlaceholderClick?(index: number): void;

  /** Callback when a context menu is requested for a waypoint */
  onPlaceholderContextMenu?(index: number): void;

  /** Callback when a context menu is requested for the path */
  onPathContextMenu?(position: Vector3): void;

  /** Callback when a placeholder is hovered */
  onHoveredPlaceholderChanged?(
    index: number | undefined,
    isPromoted: boolean,
  ): void;
};

/** @returns a single interactive odometry path  */
export const PathRenderer = forwardRef<Group, PathRendererProps>(
  function PathRenderer(
    {
      positions,
      placeholderPositions,
      poiIndices = [],
      locked = [],
      segmentFactor = POINTS_SEGMENT_FACTOR,
      isActive,
      forceTrajectoryVisible = false,
      isParentHovered = false,
      enableAutoPromotion = false,
      clippingPlanes,
      onClick,
      onPlaceholderClick,
      onPlaceholderContextMenu,
      onPathContextMenu,
      onHoveredPlaceholderChanged,
    }: PathRendererProps,
    ref,
  ): JSX.Element | null {
    const [isManuallyHovered, setIsManuallyHovered] = useState(false);
    const [hoveredId, setHoveredId] = useState<number>();
    const [pathObject, setPathObject] = useState<Group | null>(null);

    const pathBbox = useObjectBoundingBox(pathObject);

    const camera = useThree((s) => s.camera);

    const isHovered = isParentHovered || isManuallyHovered;

    const pathColor = useMemo(
      () =>
        isActive || isHovered ? new Color(cyan[400]) : new Color(cyan[200]),
      [isHovered, isActive],
    );

    // Use the first point as the trajectory position,
    const trajectoryPosition = positions[0];

    // offset all points by trajectoryPosition and create line segments
    const segments = useMemo(() => {
      const points = positions.map((p) => p.clone().sub(trajectoryPosition));

      return points.length > 1
        ? new CatmullRomCurve3(points).getPoints(points.length * segmentFactor)
        : points;
    }, [positions, segmentFactor, trajectoryPosition]);

    const placeholderPoints = useMemo(
      () =>
        (placeholderPositions ?? positions).map((p) =>
          p.clone().sub(trajectoryPosition),
        ),
      [placeholderPositions, positions, trajectoryPosition],
    );

    const onPlaceholderHovered = useCallback(
      (id: number | undefined, isPromoted: boolean) => {
        setHoveredId(id);
        onHoveredPlaceholderChanged?.(id, isPromoted);
      },
      [onHoveredPlaceholderChanged],
    );

    const isTrajectoryViewEnabled = useAppSelector(
      selectObjectVisibility(ViewObjectTypes.trajectories),
    );
    const shouldTrajectoriesBeVisible =
      forceTrajectoryVisible || isTrajectoryViewEnabled;

    const waypointsVisibilityFromStore = useAppSelector(
      selectObjectVisibility(ViewObjectTypes.waypoints),
    );

    // Promoted waypoints ignore trajectory visibility but respect the waypoints view option.
    const showPromotedWaypoints = waypointsVisibilityFromStore;
    const showOtherWaypoints = enableAutoPromotion
      ? shouldTrajectoriesBeVisible
      : waypointsVisibilityFromStore;

    const onContextMenu = useCallback(
      (ev: ThreeEvent<MouseEvent>) => {
        if (ev.delta > 1) return;

        // If a normal room Img360 is located on top of the path line,
        // since the room pano is visually on top, it has priority for the user and
        // therefore its context menu should open instead of the path's context menu.
        // Skipping `stopPropagation` lets r3f deliver the event to the room MapPlaceholders next.
        if (hasForeignWaypointIntersection(ev, pathObject)) return;

        if (onPathContextMenu) {
          ev.stopPropagation();
          onPathContextMenu(ev.point.add(trajectoryPosition));
        }
      },
      [onPathContextMenu, trajectoryPosition, pathObject],
    );

    if (positions.length === 0) return null;
    if (
      !shouldTrajectoriesBeVisible &&
      !showOtherWaypoints &&
      !showPromotedWaypoints
    ) {
      return null;
    }

    return (
      <group
        ref={mergeRefs([ref, setPathObject])}
        name={ODOMETRY_PATH_OBJECT_NAME}
        position-x={trajectoryPosition.x}
        position-z={trajectoryPosition.z}
        position-y={
          trajectoryPosition.y +
          (isActive ? LINE_HEIGHT_OFFSET_ACTIVE : LINE_HEIGHT_OFFSET)
        }
      >
        <group
          // Reset the height when raycasting, to avoid the trajectory being considered
          // closer to the camera with respect to waypoints.
          // This is important mostly for promoted keyframes when the user
          // tries to click on one of them in the minimap
          position-y={
            -(isActive ? LINE_HEIGHT_OFFSET_ACTIVE : LINE_HEIGHT_OFFSET)
          }
        >
          {segments.length > 2 &&
            shouldTrajectoriesBeVisible &&
            camera instanceof OrthographicCamera && (
              <ScreenSpaceOrthographicLineCollision
                points={segments}
                lineWidth={LINE_EVENT_WIDTH}
                onPointerEnter={() => setIsManuallyHovered(true)}
                onPointerLeave={() => setIsManuallyHovered(false)}
                onClick={(ev) => {
                  if (onClick && ev.delta <= 1) {
                    ev.point.add(trajectoryPosition);
                    onClick(ev, pathBbox ?? new Box3());
                  }
                }}
                onContextMenu={onContextMenu}
              />
            )}
        </group>
        {shouldTrajectoriesBeVisible && (
          <SmoothLineRenderer
            points={segments}
            thickness={LINE_BORDER_WIDTH}
            color={new Color("white")}
            depthTest={false}
            clippingPlanes={clippingPlanes}
            clipping={!!clippingPlanes}
            name="PathBackground"
            renderOrder={SheetModeRenderOrders.TrajectoryBackground}
          />
        )}
        <group position-y={0.1}>
          {shouldTrajectoriesBeVisible && (
            <SmoothLineRenderer
              points={segments}
              thickness={LINE_WIDTH}
              color={pathColor}
              depthTest={false}
              clippingPlanes={clippingPlanes}
              clipping={!!clippingPlanes}
              name="PathForeground"
              renderOrder={SheetModeRenderOrders.TrajectoryForeground}
            />
          )}
          {(showPromotedWaypoints || showOtherWaypoints) && (
            <OdometryPathPlaceholders
              positions={placeholderPoints}
              poiIndices={poiIndices}
              locked={locked}
              showPromoted={showPromotedWaypoints}
              showOther={showOtherWaypoints}
              enableAutoPromotion={enableAutoPromotion}
              hoveredId={hoveredId}
              isPathHovered={isHovered}
              clippingPlanes={clippingPlanes}
              onHoveredPlaceholderChanged={onPlaceholderHovered}
              onPlaceholderClick={onPlaceholderClick}
              onPlaceholderContextMenu={onPlaceholderContextMenu}
            />
          )}
        </group>
      </group>
    );
  },
);
